import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { ReaderPay, useReaders } from '../components/CardReader.tsx';
import { GiftCardPay, PointsPay, useCustomerPoints, useCustomerWallet } from '../components/Loyalty.tsx';
import { ReceiptDialog } from '../components/Receipt.tsx';
import { Button, Dialog, EmptyState, IconButton, Keypad, Skeleton } from '../components/ui.tsx';
import { t, tn } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { money } from '../lib/format.ts';
import { usePlaces } from '../lib/places.tsx';
import { device, useSession } from '../lib/session.tsx';
import type { OrderView, Register } from '../lib/types.ts';
import { useUi } from '../lib/ui-state.tsx';

/** "wallet" is only a tab here: it pays as a gift card payment from the wallet of the customer on the bill. */
type Method = 'cash' | 'card' | 'other' | 'gift_card' | 'points' | 'wallet';
interface Part {
  method: Method;
  amount: number;
  received?: number;
  /** The gift card's code, or the card reader payment that took the card. */
  reference?: string;
  /** Points used, shown on the part. */
  points?: number;
}

const METHOD_ICON: Record<Method, IconName> = { cash: 'cash', card: 'card', other: 'receipt', gift_card: 'gift', points: 'star', wallet: 'wallet' };
/** The reference that says "from the wallet of the customer on the bill". */
const WALLET = 'WALLET';

/** Finds the register this screen uses, and its open shift. */
export function useRegister(location?: string | null) {
  const { fail } = useUi();
  const { here, multi, setHere } = usePlaces();
  const [all, setAll] = useState<Register[] | null>(null);
  const [chosen, setChosen] = useState<string | null>(device.register);
  useEffect(() => {
    action<Register[]>('pos.registers', {}).then(setAll, fail);
  }, [fail]);
  // Only the tills of this location (or of the bill's location) take its payments.
  const at = location ?? (multi ? here?.id : null);
  const registers = all && at ? all.filter((r) => !r.location || r.location === at) : all;
  const register = registers?.find((r) => r.id === chosen) ?? (registers?.length === 1 ? registers[0] : undefined) ?? null;
  const choose = (id: string) => {
    device.register = id;
    setChosen(id);
    // A till stands at its register's location.
    const loc = all?.find((r) => r.id === id)?.location;
    if (loc && multi && loc !== here?.id) setHere(loc);
  };
  return { registers, register, choose, reload: () => action<Register[]>('pos.registers', {}).then(setAll, fail) };
}

/** Big keypad-driven amount entry in minor units (whole lek for ALL). */
function amountKey(current: string, k: string): string {
  if (k === 'back') return current.slice(0, -1);
  if (k === '00') return current ? `${current}00`.slice(0, 9) : current;
  return `${current}${k}`.replace(/^0+/, '').slice(0, 9);
}

/** Banknote-friendly suggestions just above the amount due. */
function quickCash(due: number): number[] {
  const unit = 100; // 1 lek in minor units
  const steps = [500, 1000, 2000, 5000, 10000].map((n) => n * unit);
  const out = new Set<number>([due]);
  for (const s of steps) {
    const up = Math.ceil(due / s) * s;
    if (up > due) out.add(up);
    if (out.size >= 4) break;
  }
  return [...out].sort((a, b) => a - b);
}

export interface PayInput {
  session: string;
  tip: number;
  payments: Omit<Part, 'points'>[];
}

/**
 * Takes the payment for a bill on this location's till. `pay` sends it somewhere else than the
 * restaurant (a hotel check-out); `next` names what comes after (back to the tables by default).
 */
export function PayDialog({
  view,
  onClose,
  onPaid,
  pay,
  next,
  cardReader = true,
  displayKeep = null,
}: {
  view: OrderView;
  onClose: () => void;
  onPaid: (v: OrderView) => void;
  pay?: (input: PayInput) => Promise<OrderView & { change: number }>;
  next?: { label: string; icon: IconName };
  /** Off where the bill is only made when the payment is taken: a reader payment belongs to one bill. */
  cardReader?: boolean;
  /** The bill the customer display goes back to when the payment is left unfinished (the till's sale). */
  displayKeep?: string | null;
}) {
  const navigate = useNavigate();
  const { can } = useSession();
  const { fail, toast } = useUi();
  const { registers, register, choose } = useRegister(view.order.location);
  const [tip, setTip] = useState(0);
  const [parts, setParts] = useState<Part[]>([]);
  const [method, setMethod] = useState<Method>('cash');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ view: OrderView; change: number } | null>(null);
  const [receipt, setReceipt] = useState(false);
  const paidRef = useRef(false);
  const keepRef = useRef(displayKeep);
  keepRef.current = displayKeep;

  // The customer display shows the amount to pay while this is open, and the thank-you once paid.
  const order = view.order.id;
  const registerId = register?.id;
  useEffect(() => {
    if (!registerId) return;
    void action('pos.display_show', { register: registerId, order, paying: true }).catch(() => undefined);
    return () => void action('pos.display_show', { register: registerId, order: paidRef.current ? null : keepRef.current, paying: false }).catch(() => undefined);
  }, [registerId, order]);

  const due = view.order.total + tip;
  const paid = parts.reduce((s, p) => s + p.amount, 0);
  const remaining = Math.max(0, due - paid);
  const entered = typed ? Number(typed) * 100 : null;
  const points = useCustomerPoints(view.order.customer);
  const walletBalance = useCustomerWallet(view.order.customer);
  const allReaders = useReaders();
  const readers = cardReader ? allReaders : [];
  const walletLeft = Math.max(0, walletBalance - parts.filter((p) => p.reference === WALLET).reduce((s, p) => s + p.amount, 0));
  const methods: Method[] = [
    'cash',
    'card',
    ...(walletBalance > 0 ? (['wallet'] as const) : []),
    'other',
    ...(can('loyalty.view') ? (['gift_card'] as const) : []),
    ...(points ? (['points'] as const) : []),
  ];
  const byCard: Record<string, number> = {};
  for (const p of parts) if (p.reference) byCard[p.reference] = (byCard[p.reference] ?? 0) + p.amount;
  const pointsUsed = parts.reduce((s, p) => s + (p.points ?? 0), 0);
  const pointsPaid = parts.filter((p) => p.method === 'points').reduce((s, p) => s + p.amount, 0);
  const willEarn = points ? Math.floor(Math.max(0, view.order.total - pointsPaid) / points.earn_per) : 0;

  const submit = async (all: Part[]) => {
    if (!register?.session) return;
    setBusy(true);
    try {
      const input = { session: register.session.id, tip, payments: all.map(({ points: _points, ...p }) => p) };
      const r = pay ? await pay(input) : await action<OrderView & { change: number }>('restaurant.pay', { order: view.order.id, ...input });
      paidRef.current = true;
      setDone({ view: r, change: r.change });
      toast(t('pay.done_toast', { n: r.order.receipt_no ?? '' }));
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };

  // One part that covers what is left completes the payment straight away.
  const take = (m: Method, amount: number, more: Partial<Part> = {}) => {
    if (amount <= 0) return;
    if (m === 'wallet') return take('gift_card', Math.min(amount, walletLeft), { reference: WALLET });
    const part: Part =
      m === 'cash'
        ? { method: 'cash', amount: Math.min(amount, remaining), received: amount }
        : { method: m, amount: Math.min(amount, remaining), ...more };
    const all = [...parts, part];
    setTyped('');
    if (all.reduce((s, p) => s + p.amount, 0) >= due) void submit(all);
    else setParts(all);
  };

  if (done) {
    return (
      <Dialog title={t('pay.done_title')} onClose={() => onPaid(done.view)} size="sm">
        <div className="pay-done">
          <span className="pay-done-icon">
            <Icon name="checkCircle" size={44} />
          </span>
          <p className="pay-done-title">{t('pay.receipt', { n: done.view.order.receipt_no ?? '' })}</p>
          {done.change > 0 ? (
            <div className="change-box" role="status">
              <span>{t('pay.give_change')}</span>
              <strong>{money(done.change)}</strong>
            </div>
          ) : (
            <p className="muted">{t('pay.no_change')}</p>
          )}
          <Button variant="primary" size="xl" icon={next?.icon ?? 'table'} className="w-full" onClick={() => onPaid(done.view)} data-autofocus>
            {next?.label ?? t('pay.next')}
          </Button>
          <Button size="lg" icon="receipt" className="w-full" onClick={() => setReceipt(true)}>
            {t('receipt.print')}
          </Button>
        </div>
        {receipt ? <ReceiptDialog order={done.view.order.id} printNow onClose={() => setReceipt(false)} /> : null}
      </Dialog>
    );
  }

  if (!registers) {
    return (
      <Dialog title={t('pay.title')} onClose={onClose}>
        <Skeleton h={240} />
      </Dialog>
    );
  }

  if (!register) {
    return (
      <Dialog title={t('pay.pick_register')} onClose={onClose} size="sm">
        {registers.length === 0 ? (
          <EmptyState icon="register" title={t('pay.no_registers')} />
        ) : (
          <div className="menu-list">
            {registers.map((r) => (
              <button key={r.id} className="menu-item" onClick={() => choose(r.id)}>
                <Icon name="register" />
                <span>
                  <strong>{r.name}</strong>
                  <span className="muted">{r.session ? t('shift.open_state') : t('shift.closed_state')}</span>
                </span>
                <Icon name="chevronRight" size={18} />
              </button>
            ))}
          </div>
        )}
      </Dialog>
    );
  }

  if (!register.session) {
    return (
      <Dialog title={t('pay.title')} onClose={onClose} size="sm">
        <EmptyState
          icon="register"
          title={t('pay.no_shift', { name: register.name })}
          body={can('pos.shift') ? t('pay.no_shift_body') : t('pay.no_shift_ask')}
          action={
            can('pos.shift') ? (
              <Button variant="primary" icon="register" onClick={() => navigate('/shift')}>
                {t('shift.open')}
              </Button>
            ) : undefined
          }
        />
      </Dialog>
    );
  }

  return (
    <Dialog
      title={t('pay.title_for', { name: view.order.name })}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="muted">
            <Icon name="register" size={14} /> {register.name}
          </span>
          {parts.length ? (
            <Button variant="ghost" icon="undo" onClick={() => setParts([])} disabled={busy}>
              {t('pay.start_over')}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="pay">
        <div className="pay-summary">
          <div className="pay-due">
            <span className="muted">{parts.length ? t('pay.left_to_pay') : t('pay.to_pay')}</span>
            <strong>{money(remaining)}</strong>
          </div>
          <dl className="totals">
            <dt>{t('totals.total')}</dt>
            <dd>{money(view.order.total)}</dd>
            <dt className="muted">{t('totals.vat_included')}</dt>
            <dd className="muted">{money(view.order.tax)}</dd>
            <dt>{t('totals.tip')}</dt>
            <dd>
              <span className="tip-row">
                {[0, 5, 10].map((p) => {
                  const v = Math.round((view.order.total * p) / 100 / 100) * 100;
                  return (
                    <button
                      key={p}
                      className={`chip${tip === v ? ' active' : ''}`}
                      aria-pressed={tip === v}
                      disabled={parts.length > 0}
                      onClick={() => setTip(v)}
                    >
                      {p === 0 ? t('pay.no_tip') : `${p}%`}
                    </button>
                  );
                })}
              </span>
            </dd>
          </dl>
          {parts.length ? (
            <ul className="pay-parts">
              {parts.map((p, i) => (
                <li key={i}>
                  <Icon name={METHOD_ICON[p.method]} size={16} />{' '}
                  {p.reference === WALLET
                    ? t('pay.method_wallet')
                    : p.method === 'gift_card' && p.reference
                    ? t('gift.card_tail', { tail: p.reference.slice(-4) })
                    : p.method === 'card' && p.reference
                      ? t('rd.part')
                    : p.points
                      ? tn('points.count', p.points)
                      : t(`pay.method_${p.method}`)}
                  <strong>{money(p.amount)}</strong>
                </li>
              ))}
            </ul>
          ) : null}
          {points && willEarn > 0 ? (
            <p className="earn-note">
              <Icon name="star" size={16} /> {tn('points.will_earn', willEarn)}
            </p>
          ) : null}
        </div>

        <div className="pay-entry">
          <div className={`segmented methods-${methods.length}`} role="tablist" aria-label={t('pay.method')}>
            {methods.map((m) => (
              <button key={m} role="tab" aria-selected={method === m} className={method === m ? 'active' : ''} onClick={() => setMethod(m)}>
                <Icon name={METHOD_ICON[m]} size={18} /> {t(`pay.method_${m}`)}
              </button>
            ))}
          </div>

          {method === 'gift_card' ? (
            <GiftCardPay remaining={remaining} used={byCard} busy={busy} onTake={(code, amount) => take('gift_card', amount, { reference: code })} />
          ) : method === 'points' && points ? (
            <PointsPay points={points} remaining={remaining} usedPoints={pointsUsed} busy={busy} onTake={(n, amount) => take('points', amount, { points: n })} />
          ) : method === 'cash' ? (
            <>
              <div className="quick-cash">
                {quickCash(remaining).map((v) => (
                  <Button key={v} size="lg" variant={v === remaining ? 'success' : 'secondary'} onClick={() => take('cash', v)} disabled={busy}>
                    {v === remaining ? t('pay.exact', { amount: money(v) }) : money(v)}
                  </Button>
                ))}
              </div>
              <div className="amount-display" aria-live="polite">
                <span className="muted">{t('pay.received')}</span>
                <strong>{entered !== null ? money(entered) : '–'}</strong>
                {entered !== null && entered > remaining ? (
                  <span className="text-ok">{t('pay.change_preview', { amount: money(entered - remaining) })}</span>
                ) : null}
              </div>
              <Keypad onKey={(k) => setTyped((x) => amountKey(x, k))} extra={{ key: '00', label: '00' }} />
              <Button
                variant="success"
                size="xl"
                icon="cash"
                className="w-full"
                busy={busy}
                disabled={entered === null || entered <= 0}
                onClick={() => entered !== null && take('cash', entered)}
              >
                {entered !== null && entered < remaining ? t('pay.take_part', { amount: money(entered) }) : t('pay.take_cash')}
              </Button>
            </>
          ) : (
            <>
              {method === 'wallet' ? (
                <p className="pay-wallet" role="status">
                  <Icon name="wallet" size={18} /> {t('pay.wallet_has', { amount: money(walletLeft) })}
                </p>
              ) : null}
              <div className="amount-display">
                <span className="muted">{t('pay.amount')}</span>
                <strong>{money(entered ?? (method === 'wallet' ? Math.min(walletLeft, remaining) : remaining))}</strong>
                {typed ? <IconButton icon="x" label={t('common.clear')} onClick={() => setTyped('')} /> : null}
              </div>
              <Keypad onKey={(k) => setTyped((x) => amountKey(x, k))} extra={{ key: '00', label: '00' }} />
              {method === 'card' && readers.length ? (
                <ReaderPay
                  readers={readers}
                  amount={entered ?? remaining}
                  purpose={{ module: 'sales', record: view.order.id }}
                  description={view.order.name}
                  disabled={busy || (entered ?? 0) > remaining}
                  onPaid={(ref, amount) => take('card', amount, { reference: ref })}
                />
              ) : null}
              <Button
                variant={method === 'card' && readers.length ? 'secondary' : 'success'}
                size={method === 'card' && readers.length ? 'lg' : 'xl'}
                icon={METHOD_ICON[method]}
                className="w-full"
                busy={busy}
                disabled={(entered ?? remaining) <= 0 || (entered ?? 0) > remaining || (method === 'wallet' && ((entered ?? 0) > walletLeft || walletLeft <= 0))}
                onClick={() => take(method, entered ?? (method === 'wallet' ? Math.min(walletLeft, remaining) : remaining))}
              >
                {method === 'card' && readers.length
                  ? t('rd.manual', { amount: money(entered ?? remaining) })
                  : t('pay.take_method', { method: t(`pay.method_${method}`), amount: money(entered ?? (method === 'wallet' ? Math.min(walletLeft, remaining) : remaining)) })}
              </Button>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
}
